import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import FieldConfigurationAPI from "../../../../lib/api/FieldConfiguration";

function getInitialCodes(filter) {
  const rules = filter?.rules;
  if (rules && rules.length > 0 && Array.isArray(rules[0].jcct?.codes)) {
    return rules[0].jcct.codes;
  }
  return [];
}

export default function CostTypesFilterModal({
  open,
  groupUuid,
  companyUuid,
  filter,
  onSave,
  onClose,
  readOnly = false,
}) {
  const [costTypes, setCostTypes] = useState([]);
  const [loadingCostTypes, setLoadingCostTypes] = useState(false);
  const [allowedCodes, setAllowedCodes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setAllowedCodes(getInitialCodes(filter));
    setError(null);
    setLoadingCostTypes(true);
    FieldConfigurationAPI.getCostTypes(companyUuid).then((response) => {
      if (response.status === 200 && Array.isArray(response.data)) {
        setCostTypes(response.data);
      } else {
        setCostTypes([]);
      }
      setLoadingCostTypes(false);
    });
  }, [open, filter, companyUuid]);

  if (!open) return null;

  function toggleCode(code) {
    setAllowedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  }

  function handleSave() {
    const rules = [{ filter_type: "include", jcct: { codes: allowedCodes } }];

    onSave({ resource: "jcct", mode: "filtered", rules });
  }

  return (
    <div className="jfm-overlay" onClick={onClose}>
      <div
        className="jfm-container wlfm-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="jfm-header">
          <span className="jfm-title">Cost Types Filters</span>
          <button className="jfm-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="jfm-body">
          <fieldset className="jfm-fieldset" disabled={readOnly}>
            {loadingCostTypes ? (
              <p style={{ color: "rgba(100,116,139,1)", fontSize: "14px" }}>
                Loading...
              </p>
            ) : costTypes.length === 0 ? (
              <p style={{ color: "rgba(100,116,139,1)", fontSize: "14px" }}>
                No cost types have been configured for this company.
              </p>
            ) : (
              <>
                {!readOnly && (
                  <div className="wlfm-all-none">
                    <button
                      className="wlfm-preset-btn"
                      onClick={() =>
                        setAllowedCodes(costTypes.map((t) => t.reference))
                      }
                    >
                      All
                    </button>
                    <button
                      className="wlfm-preset-btn"
                      onClick={() => setAllowedCodes([])}
                    >
                      None
                    </button>
                  </div>
                )}

                <table className="wlfm-table">
                  <thead>
                    <tr>
                      <th>Allow</th>
                      <th>Code</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costTypes.map(({ uuid, reference, description }) => (
                      <tr key={uuid || reference}>
                        <td>
                          <input
                            type="checkbox"
                            checked={allowedCodes.includes(reference)}
                            onChange={() => toggleCode(reference)}
                          />
                        </td>
                        <td>{reference}</td>
                        <td>{description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </fieldset>
        </div>

        {error && <div className="jfm-error">{error}</div>}

        <div className="jfm-footer">
          <div />
          <div className="jfm-footer-actions">
            {readOnly ? (
              <button className="jfm-cancel" onClick={onClose}>
                Close
              </button>
            ) : (
              <>
                <button
                  className="jfm-cancel"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="jfm-save"
                  onClick={handleSave}
                  disabled={saving || loadingCostTypes}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
